import { TranslateService } from '@ngx-translate/core';

import { isApplied } from '@core/filters/path-filter/codec';
import {
  DraftAssociation,
  DraftCondition,
  DraftGroup,
  DraftNode,
  isConditionComplete
} from '@core/filters/path-filter/draft';
import { printFilter } from '@core/filters/path-filter/dsl/printer';
import {
  FilterField,
  FilterVocabulary
} from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FilterOpName,
  OpArgument,
  SelfRef,
  edgePath,
  pathKey,
  segmentName
} from '@core/filters/path-filter/wire';

import { AvailableLanguages } from '@shared/types/translation.type';

import { BlockMode } from '../association/path-filter-association.component';
import { groupModeOf } from '../group-mode';
import {
  PATH_FILTER_I18N,
  argumentSummary,
  closedValueLabel,
  conditionFieldLabel,
  fieldLabel,
  operatorLabel,
  scopedAssociationLabel
} from '../path-filter-labels';
import { holeOf } from '../value/value-slot';

export interface PhraseSegment {
  text: string;
  role: 'plain' | 'field' | 'value' | 'negation';
  /** Id of the draft node this part of the phrase describes. */
  node: string | null;
}

const PHRASE_I18N = `${PATH_FILTER_I18N}.phrase`;

/** The operators that say the opposite of another one; the phrase marks them as negation. */
const NEGATING_OPS: ReadonlySet<FilterOpName> = new Set<FilterOpName>([
  'is_not_empty',
  'not_equals',
  'is_none_of',
  'not_contains_all',
  'not_contains',
  'not_has_tag',
  'has_none_of_tags',
  'not_has_label',
  'label_not_equals',
  'label_not_in',
  'label_references_none_of'
]);

/** Stands in for an interpolated name while a translation's markup is read. */
const NAME_MARK = '\u0000';

const MARKUP = /<(neg|field)>(.*?)<\/\1>/g;

/**
 * A clause of the phrase. `compound` clauses (groups and related entities) end in a list of their
 * own, so the next sibling is set apart with a semicolon.
 */
interface Clause {
  segments: PhraseSegment[];
  compound: boolean;
}

class PhraseWriter {
  constructor(
    private readonly vocabulary: FilterVocabulary,
    private readonly lang: AvailableLanguages,
    private readonly translate: TranslateService,
    private readonly selfLabel: string | null
  ) {}

  describe(root: DraftGroup): PhraseSegment[] {
    const subject = this.entityPlural(this.vocabulary.entityType);
    const body = this.body(root, null, root.id);
    return [
      { text: subject, role: 'field', node: root.id },
      ...(body.length ? [plain(' ', root.id), ...body] : [])
    ];
  }

  /**
   * What the entity or a related entity must satisfy: each condition opens with "where", unless
   * the whole body is negated and says so first, or the body follows a word of its own (`where`
   * false). Empty when nothing in it is finished.
   */
  body(
    group: DraftGroup,
    scope: string | null,
    owner: string,
    where = true
  ): PhraseSegment[] {
    const negated = groupModeOf(group) === 'none';
    const clauses = this.clauses(group, scope, where && !negated);
    if (clauses.length === 0) return [];
    const joined = this.joined(clauses, group.connector, owner);
    if (!negated) return joined;
    return [
      ...this.marked(`${PHRASE_I18N}.group.none`, owner),
      plain(' ', owner),
      ...joined
    ];
  }

  private entityPlural(entityType: string): string {
    const key = `${PHRASE_I18N}.entities.${entityType}`;
    const text = this.translate.instant(key) as string;
    return text === key ? entityType : text;
  }

  private clauses(group: DraftGroup, scope: string | null, where: boolean): Clause[] {
    return group.children
      .map((child) => this.clause(child, scope, where))
      .filter((clause): clause is Clause => clause !== null);
  }

  private clause(node: DraftNode, scope: string | null, where: boolean): Clause | null {
    switch (node.kind) {
      case 'condition': {
        const segments = this.condition(node, scope);
        return (
          segments && { segments: this.opened(segments, node.id, where), compound: false }
        );
      }
      case 'opaque': {
        const text = printFilter(node.negated ? { not: node.expr } : node.expr);
        const segments = [{ text, role: 'value' as const, node: node.id }];
        return { segments: this.opened(segments, node.id, where), compound: false };
      }
      case 'group':
        return this.group(node, scope);
      case 'association':
        return this.association(node, scope);
    }
  }

  private opened(segments: PhraseSegment[], node: string, where: boolean): PhraseSegment[] {
    if (!where) return segments;
    return [plain(this.word('where'), node), plain(' ', node), ...segments];
  }

  private group(group: DraftGroup, scope: string | null): Clause | null {
    const clauses = this.clauses(group, scope, false);
    if (clauses.length === 0) return null;
    return {
      segments: [
        ...this.marked(`${PHRASE_I18N}.group.${groupModeOf(group)}`, group.id),
        plain(' ', group.id),
        ...this.joined(clauses, group.connector, group.id)
      ],
      compound: true
    };
  }

  /** A block the wire drops would otherwise read as an existence filter that is not applied. */
  private association(association: DraftAssociation, scope: string | null): Clause | null {
    if (!isApplied(association)) return null;
    const id = association.id;
    const mode: BlockMode = association.negated ? 'no' : association.quantifier;
    const noun = scopedAssociationLabel(
      this.vocabulary,
      scope,
      association.edge,
      this.lang,
      this.translate
    ).toLocaleLowerCase(this.lang);
    const header = this.marked(`${PHRASE_I18N}.association.${mode}`, id, noun);
    const body = this.body(association.filter, edgePath(scope, association.edge), id);
    return {
      segments: body.length ? [...header, plain(' ', id), ...body] : header,
      compound: true
    };
  }

  /** Unfinished rows say nothing yet, so they are left out. */
  private condition(
    condition: DraftCondition,
    scope: string | null
  ): PhraseSegment[] | null {
    if (!isConditionComplete(condition) || !condition.path || !condition.op) return null;
    const { path, op, arg } = condition;
    const id = condition.id;
    const field = this.vocabulary.field(scope, path);
    const name = field
      ? conditionFieldLabel(field, path, this.lang, this.translate)
      : segmentName(path.segments[path.segments.length - 1]);
    const segments: PhraseSegment[] = [
      { text: name, role: 'field', node: id },
      plain(' ', id),
      {
        text: operatorLabel(op, this.translate),
        role: NEGATING_OPS.has(op) ? 'negation' : 'plain',
        node: id
      }
    ];
    const value = this.value(op, arg, field);
    if (value) segments.push(plain(' ', id), { text: value, role: 'value', node: id });
    return segments;
  }

  private value(
    op: FilterOpName,
    arg: OpArgument | null,
    field: FilterField | undefined
  ): string {
    const hole = holeOf(arg);
    if (hole !== null) return `‹${hole}›`;
    if (arg && typeof arg === 'object' && !Array.isArray(arg) && 'self' in arg) {
      return this.self(arg.self);
    }
    return argumentSummary(op, arg, this.translate, (value) =>
      closedValueLabel(field, value, this.lang)
    );
  }

  /**
   * A host that names no record gets sentences of their own rather than a generic noun dropped
   * into the named ones: fr/es determiners agree with their noun ("ce" vs "cet enregistrement").
   */
  private self(ref: SelfRef): string {
    const suffix = this.selfLabel === null ? '_record' : '';
    const record = this.selfLabel?.toLocaleLowerCase(this.lang);
    if ('label' in ref) {
      return this.translate.instant(`${PHRASE_I18N}.self_label${suffix}`, {
        record,
        label: ref.label
      });
    }
    const key = pathKey(ref.field);
    const field = this.vocabulary.selfFields().find((candidate) => candidate.key === key);
    const name = field ? fieldLabel(field, this.lang, this.translate) : key;
    return this.translate.instant(`${PHRASE_I18N}.self_field${suffix}`, {
      record,
      field: name
    });
  }

  private joined(
    clauses: Clause[],
    connector: 'and' | 'or',
    owner: string
  ): PhraseSegment[] {
    const word = this.word(connector);
    return clauses.flatMap((clause, index) => {
      if (index === 0) return clause.segments;
      const before = clauses[index - 1].compound ? '; ' : ' ';
      return [plain(`${before}${word} `, owner), ...clause.segments];
    });
  }

  private word(name: 'where' | 'and' | 'or'): string {
    return this.translate.instant(`${PHRASE_I18N}.words.${name}`);
  }

  /** A translation whose `<neg>` and `<field>` parts become negation and field segments. */
  private marked(key: string, node: string, name = ''): PhraseSegment[] {
    const text: string = this.translate.instant(key, { noun: NAME_MARK });
    const segments: PhraseSegment[] = [];
    let last = 0;
    for (const match of text.matchAll(MARKUP)) {
      if (match.index > last) segments.push(plain(text.slice(last, match.index), node));
      segments.push({
        text: match[2],
        role: match[1] === 'neg' ? 'negation' : 'field',
        node
      });
      last = match.index + match[0].length;
    }
    if (last < text.length) segments.push(plain(text.slice(last), node));
    return segments.map((segment) => ({
      ...segment,
      text: segment.text.replaceAll(NAME_MARK, name)
    }));
  }
}

function plain(text: string, node: string): PhraseSegment {
  return { text, role: 'plain', node };
}

/**
 * The whole filter as one sentence in the UI language: the entity's plural, then each finished
 * part, joined by its group's word. `selfLabel` names the record a `self` reference points at.
 */
export function describeFilter(
  root: DraftGroup,
  vocabulary: FilterVocabulary,
  lang: AvailableLanguages,
  translate: TranslateService,
  selfLabel: string | null = null
): PhraseSegment[] {
  return new PhraseWriter(vocabulary, lang, translate, selfLabel).describe(root);
}

/**
 * The filter's conditions without the entity they filter, for a sentence that already names what
 * they qualify ("only when …"). Empty when nothing in it is finished.
 */
export function describeConditions(
  root: DraftGroup,
  vocabulary: FilterVocabulary,
  lang: AvailableLanguages,
  translate: TranslateService
): PhraseSegment[] {
  return new PhraseWriter(vocabulary, lang, translate, null).body(
    root,
    null,
    root.id,
    false
  );
}
